import { isUUID } from "./uuid";

/**
 * The group every unreadable-payload failure is filed under.
 *
 * Reporting otherwise groups an exception by where it was thrown from, which
 * splits this one fault across as many groups as there are call sites that read
 * JSON — each looking like a small, separate problem, and none showing the
 * rate. Which request failed, and the status it answered under, ride along as
 * tags so they stay filterable inside the one group.
 */
export const MALFORMED_RESPONSE_FINGERPRINT = "malformed-response-body";

/**
 * The group an error names for its own reports, if it names one.
 *
 * Letting the raiser declare this is what keeps reporting from having to
 * recognise each fault by hand. The alternative is matching the message, and a
 * message is prose: it is reworded, it is localized, and anything else that
 * happens to word a failure the same way silently joins the group. A field the
 * raiser sets has no such collision surface, and the next fault worth grouping
 * needs no edit here at all.
 *
 * Read structurally rather than through an `instanceof`, because this runs
 * inside reporting, which the data layer depends on and so cannot depend on in
 * turn.
 */
export function declaredReportingGroup(error: unknown): string | undefined {
  const { reportingGroup } = (error ?? {}) as { reportingGroup?: unknown };

  return typeof reportingGroup === "string" ? reportingGroup : undefined;
}

/** What an API failure knows about the request behind it. */
interface ApiFailureFields {
  status?: number;
  url?: string;
}

/** Reads what an API failure knows about its own request. */
export function toApiFailureFields(error: unknown): ApiFailureFields {
  const { status, url } = (error ?? {}) as { status?: unknown; url?: unknown };

  return {
    status: typeof status === "number" ? status : undefined,
    url: typeof url === "string" ? url : undefined,
  };
}

// A path segment naming one record rather than a route. Collapsing these keeps
// the endpoint a bounded tag: left whole, a per-space id mints a new tag value
// per room, and the breakdown meant to name the failing endpoint instead lists
// every room anyone has asked about.
const NUMERIC_SEGMENT = /^\d+$/;

// How a route spells itself: an endpoint word (`available_days`), an API
// version (`v2`), or a slug (`bond-collective`) — lower case throughout,
// because every route this app names is written that way.
//
// Anything else in a path is a record id the two shapes above do not cover, and
// those are the ones that matter most. A promo code typed at checkout
// (`/promotions/SUMMERHQ50/valid`) is unbounded on its own, and the signed token
// the manage-booking link carries (`/manage_order/<token>`) is a credential:
// left whole it would be written into a faceted tag, one value per customer
// link, in a helper whose whole purpose is to keep that from happening.
const ROUTE_SEGMENT = /^[a-z0-9]+(?:[-_.][a-z0-9]+)*$/;

function isRecordSegment(segment: string): boolean {
  // The leading empty segment of a rooted path names nothing and must survive,
  // or every pattern gains a `:id` before its first word.
  if (segment === "") {
    return false;
  }

  return (
    NUMERIC_SEGMENT.test(segment) ||
    isUUID(segment) ||
    !ROUTE_SEGMENT.test(segment)
  );
}

/**
 * The route shape a request names, with record ids and query string collapsed.
 *
 * Reporting has to be able to say *which endpoint* is failing, and the raw URL
 * cannot: it differs on all but identical calls, so it belongs in a per-event
 * field rather than in anything grouped or faceted on.
 */
export function toEndpointPattern(url: string): string {
  return toPath(url)
    .split("/")
    .map((segment) => (isRecordSegment(segment) ? ":id" : segment))
    .join("/");
}

/**
 * A request safe to record against a report.
 *
 * The raw URL is not. The manage-booking link carries a signed token that
 * grants read, amend and cancel on the order, and a query string is where a
 * person's own typing ends up — one observed report carried an email address a
 * booker had pasted into the promo-code box. Neither belongs in a report that
 * is retained, searchable, and readable by everyone with access to it.
 *
 * Record ids are kept here, unlike in the endpoint pattern above: knowing which
 * space or order failed is most of the diagnosis, and an id is an identifier
 * rather than a credential. Anything that is neither a route word nor a plain
 * id is redacted, because that is the shape a token or a typed-in value takes.
 */
export function toReportableRequest(url: string): string {
  return toPath(url)
    .split("/")
    .map((segment) => (isReportableSegment(segment) ? segment : ":redacted"))
    .join("/");
}

function isReportableSegment(segment: string): boolean {
  return (
    segment === "" ||
    ROUTE_SEGMENT.test(segment) ||
    NUMERIC_SEGMENT.test(segment) ||
    isUUID(segment)
  );
}

function toPath(url: string): string {
  // A request is spelled either way: the API's own reads are keyed by a
  // root-relative path, while some callers name an absolute URL. The base only
  // exists to let the relative form parse and is never read back.
  try {
    return new URL(url, "https://endpoint.invalid").pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

/**
 * Reporting tags naming which request failed, and how it answered.
 *
 * The status is what separates the two faults that share the unreadable-payload
 * group: a success arriving truncated or half-written, against infrastructure
 * answering in the API's place with an error page. Keeping it a tag is what
 * lets the one group still tell them apart.
 */
export function apiFailureTags(
  fields: ApiFailureFields
): Record<string, string> {
  const { status, url } = fields;
  const tags: Record<string, string> = {};

  if (url) {
    tags.api_failure_endpoint = toEndpointPattern(url);
  }
  if (status !== undefined) {
    tags.api_failure_status = String(status);
  }

  return tags;
}
